import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { Theme } from '~/eds/src/types/Theme';

import { CountBadge } from '~/packages/eds/src/components/CountBadge/CountBadge';
import { Icon } from '~/packages/eds/src/components/Icon/Icon';
import { Popover } from '~/packages/eds/src/components/Popover/Popover';
import { PopoverContent } from '~/packages/eds/src/components/Popover/subcomponents/PopoverContent';
import { PopoverTrigger } from '~/packages/eds/src/components/Popover/subcomponents/PopoverTrigger';
import { SegmentedControl } from '~/packages/eds/src/components/SegmentedControl/SegmentedControl';
import { Text } from '~/packages/eds/src/components/Text/Text';

import type { PlatformNotification } from './types';

import { useNotificationCenter } from './NotificationCenterContext';
import { NotificationRow } from './NotificationRow';
import './NotificationTray.css';

const messages = defineMessages({
  trigger: {
    id: 'NotificationTray.trigger',
    defaultMessage: 'Notifications',
    description: 'Accessible label and title for the notifications tray',
  },
  all: {
    id: 'NotificationTray.all',
    defaultMessage: 'All',
    description: 'Tab that shows all notifications',
  },
  unread: {
    id: 'NotificationTray.unread',
    defaultMessage: 'Unread',
    description: 'Tab that shows only unread notifications',
  },
  filter: {
    id: 'NotificationTray.filter',
    defaultMessage: 'Filter notifications',
    description: 'Accessible label for the notifications filter control',
  },
  empty: {
    id: 'NotificationTray.empty',
    defaultMessage: 'No notifications',
    description: 'Empty state shown when there are no notifications to display',
  },
});

type NotificationFilter = 'all' | 'unread';

type NotificationTrayProps = {
  appearance?: 'header' | 'nav-pill';
  theme?: Theme;
};

export function NotificationTray({
  appearance = 'header',
  theme = 'light',
}: NotificationTrayProps) {
  const intl = useIntl();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const { notifications, count, unreadCount, isOpen, setOpen } =
    useNotificationCenter();

  if (count === 0) {
    return null;
  }

  const label = intl.formatMessage(messages.trigger);
  const filterSegments = [
    {
      segmentKey: 'all',
      text: intl.formatMessage(messages.all),
      label: intl.formatMessage(messages.all),
    },
    {
      segmentKey: 'unread',
      text: intl.formatMessage(messages.unread),
      label: intl.formatMessage(messages.unread),
    },
  ];
  const visibleNotifications =
    filter === 'unread'
      ? notifications.filter((notification) => !notification.isRead)
      : notifications;

  const handleClick = (notification: PlatformNotification) => {
    notification.onMarkAsRead?.();
    setOpen(false);
  };

  return (
    <span
      className="notification-tray__trigger"
      data-appearance={appearance}
      data-theme={theme}
    >
      <Popover direction="bottom-end" open={isOpen} onOpenChange={setOpen}>
        <PopoverTrigger>
          <button
            aria-label={label}
            className="notification-tray__icon-btn"
            type="button"
          >
            <Icon icon="alert" size="m" />
          </button>
        </PopoverTrigger>
        <PopoverContent label={label}>
          <div className="notification-tray__panel">
            <div className="notification-tray__header">
              <Text as="h2" variant="body-semi-l">
                {label}
              </Text>
            </div>
            <div className="notification-tray__tabs">
              <SegmentedControl
                fullWidth
                label={intl.formatMessage(messages.filter)}
                segments={filterSegments}
                size="xs"
                value={filter}
                onChange={(segmentKey) =>
                  setFilter(segmentKey as NotificationFilter)
                }
              />
            </div>
            {visibleNotifications.length === 0 ? (
              <div className="notification-tray__empty">
                <Text tone="subdued" variant="body-m">
                  {intl.formatMessage(messages.empty)}
                </Text>
              </div>
            ) : (
              <ol className="notification-tray__list">
                {visibleNotifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={handleClick}
                  />
                ))}
              </ol>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {unreadCount > 0 && (
        <span className="notification-tray__badge" role="status">
          <CountBadge count={unreadCount} />
        </span>
      )}
    </span>
  );
}
